import React from "react";
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Info, CheckCircle, XCircle } from "lucide-react";

/**
 * Reusable Confirm Dialog Component
 * 
 * @param {object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {string} props.title - Dialog title
 * @param {string} props.description - Dialog description/message
 * @param {string} props.type - Dialog type: 'danger', 'warning', 'info', 'success'
 * @param {string} props.confirmText - Text for confirm button (default from i18n)
 * @param {string} props.cancelText - Text for cancel button (default from i18n)
 * @param {function} props.onConfirm - Callback when confirm is clicked
 * @param {function} props.onCancel - Callback when cancel is clicked
 * @param {boolean} props.showCancel - Show cancel button (default: true)
 * @param {boolean} props.loading - Show loading state on confirm button
 */
export const ConfirmDialog = ({
  open,
  onOpenChange,
  title,
  description,
  type = "warning",
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  showCancel = true,
  loading = false,
}) => {
  const { t } = useTranslation();
  const defaultConfirmText = confirmText || t('common.confirm');
  const defaultCancelText = cancelText || t('common.cancel');
  const handleConfirm = () => {
    if (onConfirm) onConfirm();
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    if (onOpenChange) onOpenChange(false);
  };

  const getIcon = () => {
    switch (type) {
      case "danger":
        return <XCircle className="w-6 h-6 text-red-600" />;
      case "warning":
        return <AlertTriangle className="w-6 h-6 text-yellow-600" />;
      case "info":
        return <Info className="w-6 h-6 text-blue-600" />;
      case "success":
        return <CheckCircle className="w-6 h-6 text-green-600" />;
      default:
        return <AlertTriangle className="w-6 h-6 text-yellow-600" />;
    }
  };

  const getConfirmVariant = () => {
    switch (type) {
      case "danger":
        return "destructive";
      case "warning":
        return "destructive";
      case "success":
        return "default";
      case "info":
        return "default";
      default:
        return "default";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-3">
            {getIcon()}
            <DialogTitle>{title}</DialogTitle>
          </div>
        </DialogHeader>
        <DialogDescription className="text-sm text-muted-foreground pt-2">
          {description}
        </DialogDescription>
        <div className="flex justify-end gap-2 pt-4">
          {showCancel && (
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={loading}
            >
              {defaultCancelText}
            </Button>
          )}
          <Button
            variant={getConfirmVariant()}
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? t('common.pleaseWait') : defaultConfirmText}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDialog;
